import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useCallback } from "react";

gql(`
fragment JobFragment on Job {
    id
    task
    status
    parameters
    createdAt
    createdBy
    updatedAt
    output
    tenant {
      id
      name
    }
    document {
      id
      title
    }
    facility {
      id
      name
    }
    updatedBy
    communication {
      id
      status
    }
}
`);

export const STAFF_JOB_DIALOG_QUERY = gql(`
    query StaffJobDialog($id: ID!) {
      job(id: $id) {
        record
        ...JobFragment
      }
    }
  `);

export const STAFF_JOBS_TABLE_QUERY = gql(`
    query StaffJobsTable($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
      jobs(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...JobFragment
        }
        count
      }
    }
  `);

export const STAFF_JOB_STEPS_DONE_TABLE_QUERY = gql(`
  query StaffJobStepsDoneTable($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
      jobStepsDone(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
          items {
            id
            jobId
            name
            updatedAt
            createdAt
          }
          count
      }
      }
  `);

export const CAN_REQUEUE_JOBS_QUERY = gql(`
  query CanRequeueJobs {
    canRequeueJobs
  }
`);

export const REQUEUE_JOB_MUTATION = gql(`
  mutation RequeueJob($id: ID!) {
    requeueJob(id: $id) {
      ...JobFragment
    }
  }
`);

export const useRequeueJob = (onRequeued?: () => void) => {
  const alerts = useAlerts();
  const [mutate, { loading }] = useMutation(REQUEUE_JOB_MUTATION);

  const requeue = useCallback(
    async (id: string) => {
      try {
        await mutate({ variables: { id } });
        alerts.success("Job re-queued");
        onRequeued?.();
      } catch (e) {
        alerts.error(e instanceof Error ? e.message : "Failed to re-queue job");
      }
    },
    [alerts, mutate, onRequeued]
  );

  return { requeue, loading };
};

export const STAFF_JOB_LOGS_TABLE_QUERY = gql(`
  query StaffJobLogsTable($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
      jobLogs(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
          items {
            id
            status
            createdAt
            message
            stackTrace
            updatedAt
          }
          count
        }
      }
  `);
